import React from 'react';
import { useTranslation } from 'react-i18next';
import { reportError } from '@/lib/error-handling';
import { captureSentryException, isSentryInitialized } from '@/lib/sentry';

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
};

export class ErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    reportError('ErrorBoundary caught an error:', error, {
      source: 'error_boundary',
      componentStack: errorInfo.componentStack,
    });
    if (isSentryInitialized()) {
      captureSentryException(error, {
        source: 'error_boundary',
        componentStack: errorInfo.componentStack,
      });
    }
    this.setState({ errorInfo });
  }

  handleReload = () => {
    /* istanbul ignore else */
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      const shouldShowDetails = import.meta.env.DEV;
      return (
        <ErrorFallback
          onReload={this.handleReload}
          shouldShowDetails={shouldShowDetails}
          error={this.state.error}
        />
      );
    }

    return this.props.children;
  }
}

function ErrorFallback({
  onReload,
  shouldShowDetails,
  error,
}: {
  onReload: () => void;
  shouldShowDetails: boolean;
  error?: Error;
}) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-2xl font-semibold">{t('company.errorBoundary.title')}</h1>
      <p className="text-muted-foreground">{t('company.errorBoundary.description')}</p>
      {shouldShowDetails && error && (
        <pre className="max-h-48 w-full overflow-auto rounded-md bg-muted p-3 text-left text-xs">
          {error.toString()}
        </pre>
      )}
      <button
        onClick={onReload}
        className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
      >
        {t('company.errorBoundary.reloadButton')}
      </button>
    </div>
  );
}
